import { ApolloError } from '@apollo/client';

export const MAX_NOT_YET_VISIBLE_ATTEMPTS = 3;

export type VerifyAndSubmitFailure =
  | { kind: 'verification-failed'; chargeId: string }
  | { kind: 'not-yet-visible'; chargeId: string; attempts: number }
  | { kind: 'attempts-exhausted'; chargeId: string }
  | { kind: 'not-verifiable'; chargeId: string }
  | { kind: 'service-request-not-complete'; chargeId?: undefined }
  | { kind: 'generic'; chargeId?: string; attempts?: number };

type FailureExtensions = { code?: unknown; chargeId?: unknown };

const readExtensions = (error: unknown): FailureExtensions =>
  error instanceof ApolloError
    ? (error.graphQLErrors[0]?.extensions ?? {})
    : {};

const asChargeId = (value: unknown) =>
  typeof value === 'string' && value !== '' ? value : undefined;

// Attempts survive an intervening generic failure, otherwise a flaky network
// would make the per-charge attempt limit unbounded.
const previousAttempts = (
  chargeId: string,
  previous?: VerifyAndSubmitFailure
) =>
  previous?.chargeId === chargeId && 'attempts' in previous
    ? (previous.attempts ?? 0)
    : 0;

const carryAttempts = (
  chargeId: string,
  previous?: VerifyAndSubmitFailure
): { attempts?: number } => {
  const attempts = previousAttempts(chargeId, previous);

  return attempts > 0 ? { attempts } : {};
};

const classifyNotYetVisible = (
  chargeId: string,
  previous?: VerifyAndSubmitFailure
): VerifyAndSubmitFailure => {
  const attempts = previousAttempts(chargeId, previous) + 1;

  return attempts >= MAX_NOT_YET_VISIBLE_ATTEMPTS
    ? { kind: 'attempts-exhausted', chargeId }
    : { kind: 'not-yet-visible', chargeId, attempts };
};

export function classifyFailure(
  error: unknown,
  previous?: VerifyAndSubmitFailure
): VerifyAndSubmitFailure {
  const { code, chargeId: reportedChargeId } = readExtensions(error);
  const chargeId = asChargeId(reportedChargeId);

  if (chargeId) {
    switch (code) {
      case 'CHARGE_SUBMITTED_VERIFICATION_FAILED':
        return { kind: 'verification-failed', chargeId };
      case 'CHARGE_NOT_YET_VISIBLE':
        return classifyNotYetVisible(chargeId, previous);
      case 'CHARGE_NOT_VERIFIABLE':
        return { kind: 'not-verifiable', chargeId };
      default:
        return {
          kind: 'generic',
          chargeId,
          ...carryAttempts(chargeId, previous),
        };
    }
  }

  // A retry that fails without a charge id must not let the user submit a second charge.
  if (previous?.chargeId) {
    return {
      kind: 'generic',
      chargeId: previous.chargeId,
      ...carryAttempts(previous.chargeId, previous),
    };
  }

  return code === 'SERVICE_REQUEST_NOT_COMPLETE'
    ? { kind: 'service-request-not-complete' }
    : { kind: 'generic' };
}

export function canRetry(failure: VerifyAndSubmitFailure) {
  return (
    failure.kind !== 'attempts-exhausted' && failure.kind !== 'not-verifiable'
  );
}
